import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN, REGISTER } from "@/constants/testIds";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useApp();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      login(r.data.token, r.data.user);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to continue your journey.">
      <form onSubmit={submit} className="space-y-6">
        <div>
          <Label className="text-xs uppercase tracking-label">Email</Label>
          <Input type="email" required data-testid={LOGIN.emailInput} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-label">Password</Label>
          <Input type="password" required data-testid={LOGIN.passwordInput} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <button type="submit" disabled={loading} data-testid={LOGIN.submitButton} className="w-full py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn disabled:opacity-50">
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-sm text-muted-foreground">
          New here? <Link to="/register" data-testid={LOGIN.registerLink} className="text-primary underline-offset-4 hover:underline">Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const Register = () => {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useApp();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/register", { name: form.name, email: form.email, password: form.password });
      login(r.data.token, r.data.user);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AuthShell title="Begin your journey" subtitle="Create an account to save readings & orders.">
      <form onSubmit={submit} className="space-y-6">
        <div>
          <Label className="text-xs uppercase tracking-label">Full name</Label>
          <Input required data-testid={REGISTER.nameInput} value={form.name} onChange={set("name")} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-label">Email</Label>
          <Input type="email" required data-testid={REGISTER.emailInput} value={form.email} onChange={set("email")} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-label">Password</Label>
          <Input type="password" required data-testid={REGISTER.passwordInput} value={form.password} onChange={set("password")} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-label">Confirm password</Label>
          <Input type="password" required data-testid={REGISTER.passwordConfirmInput} value={form.confirm} onChange={set("confirm")} className="mt-2 rounded-none border-0 border-b bg-transparent" />
        </div>
        <button type="submit" disabled={loading} data-testid={REGISTER.submitButton} className="w-full py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn disabled:opacity-50">
          {loading ? "Creating…" : "Create account"}
        </button>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" data-testid={REGISTER.loginLink} className="text-primary underline-offset-4 hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
};

const AuthShell = ({ title, subtitle, children }) => (
  <div className="max-w-md mx-auto px-6 sm:px-10 py-20">
    <p className="text-xs uppercase tracking-label text-secondary">AstroSamagri</p>
    <h1 className="display-serif text-4xl mt-3">{title}</h1>
    <p className="text-sm text-muted-foreground mt-2">{subtitle}</p>
    <div className="mt-10">{children}</div>
  </div>
);
